import { useEffect, useState } from "react";
import { createFileRoute, Link, Outlet, redirect, useNavigate, useRouter, useRouterState, type ErrorComponentProps } from "@tanstack/react-router";
import { useAdminSession } from "@/hooks/use-admin-session";
import { Logo } from "@/components/Logo";
import { IdleLogoutGuard } from "@/components/auth/IdleLogoutGuard";
import { ChevronDown, LoaderCircle, LogOut, Menu, X } from "lucide-react";
import {
  ADMIN_NAV_FOOTER,
  findAdminNavContext,
  findAdminNavGroup,
  isAdminPathAllowed,
  visibleAdminGroups,
  ADMIN_PLACEHOLDER_HINT,
  adminNavLinkClass,
  type AdminNavItem,
} from "@/lib/admin-nav";
import { loadAdminNavOpenGroups, saveAdminNavOpenGroups } from "@/lib/admin-nav-storage";
import {
  chunkErrorAction,
  clearChunkReloadFlag,
  type ChunkErrorAction,
} from "@/lib/chunk-load";

import { adminNavIcon } from "@/components/admin/admin-nav-icons";
import { AdminTabs } from "@/components/admin/AdminTabs";
import { ConfirmDialogHost } from "@/components/admin/ConfirmDialogHost";
import { Button } from "@/components/ui/button";



export const Route = createFileRoute("/_admin")({
  ssr: false,
  pendingMs: 100,
  pendingMinMs: 300,
  head: () => ({
    meta: [
      { title: "Admin – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  beforeLoad: async ({ location }) => {
    // Dynamischer Import: hält supabase-js aus dem Einstiegs-Bundle der
    // öffentlichen Seiten heraus (beforeLoad ist immer Teil des Entry-Chunks).
    const { ensureInternalAccess } = await import("@/lib/internal-access");
    const access = await ensureInternalAccess();

    switch (access.state) {
      case "needs-login":
        throw redirect({ to: "/intern", search: { redirect: location.href } });
      case "needs-cockpit":
        throw redirect({ to: "/cockpit" });
      case "needs-onboarding":
        throw redirect({ href: "/intern/reset-password?flow=invite", replace: true });
      case "needs-mfa":
        throw redirect({ to: "/intern/mfa", search: { redirect: location.href } });
      case "unknown":
        // Technische Störung (Netz, kurzzeitiger Serverfehler): angemeldet
        // bleiben, Hinweis in der Shell anzeigen – NIEMALS auf die 2FA-Seite
        // schicken, das löste bisher unnötige Codes und Wartesperren aus.
        console.warn("[admin] Zugangsprüfung unklar:", access.reason);
        return { adminUser: access.user, accessCheckFailed: true as const };
      default:
        // User an den Component-Tree weiterreichen, damit useAdminSession
        // keinen zweiten auth.getUser()-Round-Trip mehr braucht.
        return { adminUser: access.user, accessCheckFailed: false as const };
    }
  },
  // Beim Betreten des internen Bereichs die zwei am häufigsten gebrauchten
  // Datensätze im Hintergrund vorwärmen: Kennzahlen und Kundenliste.
  loader: ({ context }) => {
    if (typeof window === "undefined") return;
    void (async () => {
      const [{ warmAdminQuery }, dash, customers] = await Promise.all([
        import("@/lib/admin-loader"),
        import("@/lib/admin-dashboard.functions"),
        import("@/lib/customers.functions"),
      ]);
      warmAdminQuery(context.queryClient, {
        queryKey: ["admin", "dashboard-stats"],
        queryFn: () => dash.getAdminDashboardStats(),
        staleTime: 60_000,
      });
      warmAdminQuery(context.queryClient, {
        queryKey: ["admin", "clients"],
        queryFn: () => customers.listClients(),
      });
    })().catch(() => {
      // Vorwärmen ist optional; Fehler behandeln die Seiten selbst.
    });
  },
  pendingComponent: AdminAccessPending,
  errorComponent: AdminAccessError,
  component: AdminShell,
});

function AdminAccessPending() {
  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="border-b border-border">
        <div className="flex h-14 items-center px-6">
          <Logo />
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-6 py-16">
        <div className="flex items-center gap-3 text-sm text-muted-foreground" role="status">
          <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden />
          Zugang zum internen Bereich wird geprüft …
        </div>
      </main>
    </div>
  );
}

function AdminAccessError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  // Abgebrochenes Nachladen eines Seitenteils ist kein Zugangsfehler: einmal
  // neu laden, und nur wenn das nicht hilft, die zutreffende Meldung zeigen.
  const [action] = useState<ChunkErrorAction>(() =>
    chunkErrorAction(error, typeof window === "undefined" ? null : window.sessionStorage),
  );

  useEffect(() => {
    console.error(error);
  }, [error]);

  useEffect(() => {
    if (action !== "reload") return;
    window.location.reload();
  }, [action]);

  if (action === "reload") return <AdminAccessPending />;

  const isChunk = action === "chunk-message";

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="border-b border-border">
        <div className="flex h-14 items-center px-6">
          <Logo />
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-6 py-16">
        <div className="w-full max-w-md text-center">
          <h1 className="font-display text-3xl font-extrabold">
            {isChunk ? "Seitenteil konnte nicht geladen werden" : "Interner Bereich nicht erreichbar"}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {isChunk
              ? "Ein Teil dieser Seite ließ sich gerade nicht nachladen. Ihre Sitzung bleibt unverändert – bitte versuchen Sie es erneut."
              : "Die Zugangsprüfung konnte gerade nicht abgeschlossen werden. Ihre Sitzung bleibt unverändert."}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button
              onClick={() => {
                if (isChunk) {
                  clearChunkReloadFlag(typeof window === "undefined" ? null : window.sessionStorage);
                  window.location.reload();
                  return;
                }
                router.invalidate();
                reset();
              }}
            >
              Erneut versuchen
            </Button>
            <Button asChild variant="outline">
              <Link to="/intern">Zur Anmeldung</Link>
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}


function AdminShell() {
  const session = useAdminSession();
  const navigate = useNavigate();
  const router = useRouter();
  const { accessCheckFailed } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Die Hülle steht: ein späteres abgebrochenes Nachladen darf wieder einmal
  // neu laden.
  useEffect(() => {
    clearChunkReloadFlag(window.sessionStorage);
  }, []);

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const activeGroup = findAdminNavGroup(pathname);
  const [openGroups, setOpenGroups] = useState<Set<string>>(
    () => new Set(activeGroup ? [activeGroup] : []),
  );
  const [hydratedGroups, setHydratedGroups] = useState(false);

  // Die Gruppe der aktuellen Seite ist offen – berechnet beim Rendern, nicht
  // per Effekt. Ein Effekt öffnete sie erst nach dem Bildaufbau, dadurch
  // fehlten die Unterpunkte der neuen Seite kurz nach jedem Wechsel.
  // Wird sie bewusst zugeklappt, bleibt das für die aktuelle Seite bestehen
  // (sonst ließe sich die aktive Gruppe nie schließen).
  const [closedActiveFor, setClosedActiveFor] = useState<string | null>(null);
  const visibleOpenGroups =
    activeGroup && closedActiveFor !== pathname
      ? new Set([...openGroups, activeGroup])
      : openGroups;

  // Gespeicherten Zustand erst nach der Hydration übernehmen (kein SSR-Mismatch).
  useEffect(() => {
    const stored = loadAdminNavOpenGroups();
    if (stored) {
      setOpenGroups(new Set(activeGroup ? [...stored, activeGroup] : stored));
    }
    setHydratedGroups(true);
    // Nur einmal beim Mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Jede Änderung sofort lokal sichern.
  useEffect(() => {
    if (!hydratedGroups) return;
    saveAdminNavOpenGroups(openGroups);
  }, [openGroups, hydratedGroups]);

  // Mobiles Menü bei Seitenwechsel schließen und den Hintergrund nicht mitscrollen.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileNavOpen]);




  const handleSignOut = async () => {
    await session.signOut();
    navigate({ to: "/intern", replace: true });
  };



  // Login durchgelaufen, aber Nutzer hat weder Admin- noch Editor-Rolle:
  if (session.status === "authenticated" && !session.isAdmin && !session.isEditor) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <div className="max-w-md text-center">
          <h1 className="font-display text-3xl font-extrabold">Kein Zugriff</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Ihr Konto hat keine Backend-Berechtigung. Bitte wenden Sie sich an einen Administrator.
          </p>
          <button
            onClick={async () => {
              await session.signOut();
              navigate({ to: "/intern", replace: true });
            }}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground"
          >
            <LogOut className="h-4 w-4" /> Abmelden
          </button>
        </div>
      </div>
    );
  }

  const isVisible = (i: AdminNavItem) => !i.adminOnly || session.isAdmin;
  const navContext = findAdminNavContext(pathname);
  // Ticket AD3, Punkt 8: gesperrte Adresse direkt aufgerufen. Die Freigabe kommt
  // aus der Navigationsdefinition; nicht verlinkte Adressen erben die
  // Rollenbedingung ihres Bereichs (siehe ADMIN_UNLINKED).
  const areaAllowed =
    session.status !== "authenticated" ||
    isAdminPathAllowed(pathname, { isAdmin: session.isAdmin });
  const isActive = (i: AdminNavItem) => navContext.item?.key === i.key;

  const linkClass = (active: boolean) =>
    "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors " +
    (active
      ? "bg-secondary text-foreground"
      : "text-muted-foreground hover:bg-secondary hover:text-foreground");

  const renderItem = (item: AdminNavItem, onNavigate?: () => void) => {
    const Icon = adminNavIcon(item.icon);
    const active = isActive(item);
    return (
      <Link
        key={item.key}
        to={item.to}
        className={adminNavLinkClass(linkClass, { active, muted: item.muted })}
        onClick={onNavigate}
      >
        <Icon className="h-4 w-4" /> {item.label}
        {item.muted ? <span className="sr-only"> – {ADMIN_PLACEHOLDER_HINT}</span> : null}
      </Link>
    );
  };


  const renderGroups = (onNavigate?: () => void) => (
    <>
      {visibleAdminGroups({ isAdmin: session.isAdmin }).map((group, groupIndex) => {
        const hasHeader = group.key !== "start";
        const isOpen = !hasHeader || visibleOpenGroups.has(group.key);
        return (
          <div
            key={group.key}
            className={groupIndex === 0 ? "" : "mt-4 border-t border-border pt-3"}
          >
            {hasHeader && (
              <button
                type="button"
                onClick={() => {
                  setClosedActiveFor(
                    isOpen && group.key === activeGroup ? pathname : null,
                  );
                  setOpenGroups((prev) => {
                    const next = new Set(prev);
                    if (isOpen) {
                      next.delete(group.key);
                    } else {
                      next.add(group.key);
                    }
                    return next;
                  });
                }}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-base font-semibold text-brand-petrol hover:bg-secondary"
              >
                <span>{group.label}</span>
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`}
                  aria-hidden="true"
                />
              </button>
            )}
            {isOpen && (
              <div className={hasHeader ? "mt-1 flex flex-col gap-1" : "flex flex-col gap-1"}>
                {group.items.map((item) => renderItem(item, onNavigate))}
              </div>
            )}
          </div>
        );
      })}
      <div className="mt-4 border-t border-border pt-3">
        {ADMIN_NAV_FOOTER.filter(isVisible).map((item) => renderItem(item, onNavigate))}
      </div>

    </>
  );


  return (
    <div className="flex min-h-dvh flex-col bg-background">
      {/* Automatische Abmeldung nach 30 Minuten Inaktivität (Ticket H2). */}
      <IdleLogoutGuard idleMinutes={30} onTimeout={handleSignOut} />
      {/* Gebrandete Rückfragen statt Browser-Dialoge. */}
      <ConfirmDialogHost />
      {accessCheckFailed && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-center gap-3 border-b border-border bg-secondary px-6 py-2 text-sm"
        >
          <span>
            Der Anmeldestatus konnte gerade nicht vollständig geprüft werden. Sie
            bleiben angemeldet.
          </span>
          <button
            type="button"
            onClick={() => router.invalidate()}
            className="rounded-md border border-border px-3 py-1 text-xs font-semibold hover:bg-background"
          >
            Erneut prüfen
          </button>
        </div>
      )}

      <a
        href="#admin-inhalt"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-brand-button focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-brand-button-foreground"
      >
        Direkt zum Inhalt
      </a>
      <header className="sticky top-0 z-30 border-b border-border bg-background">
        <div className="flex h-14 items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileNavOpen((v: boolean) => !v)}
              aria-expanded={mobileNavOpen}
              aria-controls="admin-mobilnavigation"
              aria-label={mobileNavOpen ? "Menü schließen" : "Menü öffnen"}
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md border border-border text-foreground hover:bg-secondary md:hidden"
            >
              {mobileNavOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
            <Logo />
            <span className="rounded bg-secondary px-2 py-0.5 text-xs font-semibold text-muted-foreground">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden text-xs text-muted-foreground sm:inline">
              {session.email}
            </span>
            <button
              onClick={handleSignOut}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary"
            >
              <LogOut className="h-3.5 w-3.5" /> Abmelden
            </button>
          </div>
        </div>
        {mobileNavOpen && (
          <nav
            id="admin-mobilnavigation"
            aria-label="Bereiche"
            className="flex max-h-[calc(100dvh-3.5rem)] flex-col gap-1 overflow-y-auto border-t border-border p-4 md:hidden"
          >
            {renderGroups(() => setMobileNavOpen(false))}
          </nav>
        )}
      </header>

      <div className="flex min-w-0 flex-1">
        <aside className="hidden w-56 shrink-0 border-r border-border md:block">
          <nav
            aria-label="Bereiche"
            className="sticky top-14 flex max-h-[calc(100dvh-3.5rem)] flex-col gap-1 overflow-y-auto p-4"
          >
            {renderGroups()}
          </nav>
        </aside>


        <main id="admin-inhalt" className="min-w-0 flex-1 px-6 py-10 md:px-10">

          {areaAllowed ? (
            <>
              <AdminTabs isAdmin={session.isAdmin} />
              <Outlet />
            </>
          ) : (
            <div className="max-w-xl">
              <h1 className="font-display text-3xl font-extrabold md:text-4xl">
                Dieser Bereich ist für Ihre Rolle nicht freigegeben
              </h1>
              <p className="mt-3 text-sm text-muted-foreground">
                Als Editorin oder Editor haben Sie hier keinen Zugriff. Bitte wenden Sie
                sich an einen Administrator, wenn Sie diese Ansicht benötigen.
              </p>
              <Link
                to="/admin"
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground"
              >
                Zurück zum Dashboard
              </Link>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
